import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { postData } from "../../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// THEME STYLES - Syncs with Dashboard Theme
// ─────────────────────────────────────────────────────────────────────────────
const getThemeStyles = (isDark) => ({
  dark: {
    background: "#0d1117",
    surface: "#141824",
    surfaceLighter: "#1a2035",
    surfaceLightest: "#1e2740",
    border: "#1e2740",
    borderLight: "#2a3145",
    textPrimary: "#f1f5f9",
    textSecondary: "#94a3b8",
    textMuted: "#64748b",
    primary: "#3b82f6",
    primaryDark: "#2563eb",
    primaryLight: "#60a5fa",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    gradient: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
    inputBg: "#0d1117",
    placeholder: "#4a4f6e",
  },
  light: {
    background: "#f8fafc",
    surface: "#ffffff",
    surfaceLighter: "#f1f5f9",
    surfaceLightest: "#f8fafc",
    border: "#e2e8f0",
    borderLight: "#cbd5e1",
    textPrimary: "#0f172a",
    textSecondary: "#334155",
    textMuted: "#64748b",
    primary: "#6366f1",
    primaryDark: "#4f46e5",
    primaryLight: "#818cf8",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444",
    gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    inputBg: "#ffffff",
    placeholder: "#94a3b8",
  },
});

// Icons
const BackIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>);
const UploadIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>);
const VegIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>);
const NonVegIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="currentColor"/><path d="M12 2v20"/></svg>);
const CloseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>);

const AddMenuItem = ({ isDark = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state or sessionStorage (for refresh)
  const getDesignationId = () => {
    if (location.state?.designation_id) return location.state.designation_id;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.designation_id;
      } catch(e) { return ""; }
    }
    return "";
  };
  
  const getRestaurantName = () => {
    if (location.state?.restaurantName) return location.state.restaurantName;
    const stored = sessionStorage.getItem('selectedRestaurant');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.restaurantName;
      } catch(e) { return ""; }
    }
    return "";
  };
  
  const designation_id = getDesignationId();
  const restaurantName = getRestaurantName();
  
  const ts = getThemeStyles(isDark)[isDark ? 'dark' : 'light'];
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    main_category: "",
    category: "",
    sub_category: "",
    is_veg: "yes",
    pic1: null,
    pic1_preview: null,
  });
  
  // Dropdown data
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Save to sessionStorage for persistence
  useEffect(() => {
    if (designation_id) {
      sessionStorage.setItem('selectedRestaurant', JSON.stringify({
        designation_id: designation_id,
        restaurantName: restaurantName
      }));
    }
  }, [designation_id, restaurantName]);
  
  // Fetch main categories on load
  useEffect(() => {
    fetchMainCategories();
  }, []);
  
  // Fetch main categories from backend
  const fetchMainCategories = async () => {
    setLoading(true);
    try {
      const response = await postData("categories/main_categories", {});
      console.log("Main Categories Response:", response);
      
      let categoriesData = [];
      if (response?.success && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      } else if (response?.categories && Array.isArray(response.categories)) {
        categoriesData = response.categories;
      }
      
      setMainCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching main categories:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch categories based on selected main category
  const fetchCategories = async (mainCategory) => {
    if (!mainCategory) {
      setCategories([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await postData("categories/categories", { 
        main_category: mainCategory 
      });
      console.log("Categories Response:", response);
      
      let categoriesData = [];
      if (response?.success && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      } else if (response?.categories && Array.isArray(response.categories)) {
        categoriesData = response.categories;
      }
      
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch sub categories based on selected category
  const fetchSubCategories = async (category) => {
    if (!category) {
      setSubCategories([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await postData("categories/sub_categories", { 
        category: category 
      });
      console.log("Sub Categories Response:", response);
      
      let subCategoriesData = [];
      if (response?.success && Array.isArray(response.data)) {
        subCategoriesData = response.data;
      } else if (Array.isArray(response)) {
        subCategoriesData = response;
      } else if (response?.sub_categories && Array.isArray(response.sub_categories)) {
        subCategoriesData = response.sub_categories;
      }
      
      setSubCategories(subCategoriesData);
    } catch (error) {
      console.error("Error fetching sub categories:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle main category change
  const handleMainCategoryChange = (value) => {
    setFormData({ ...formData, main_category: value, category: "", sub_category: "" });
    if (value) {
      fetchCategories(value);
    } else {
      setCategories([]);
      setSubCategories([]);
    }
  };
  
  // Handle category change
  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value, sub_category: "" });
    if (value) {
      fetchSubCategories(value);
    } else {
      setSubCategories([]);
    }
  };
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Process image function
  const processImage = (file) => {
    if (file.size > 2 * 1024 * 1024) {
      setImageError("Image size should be less than 2MB");
      return false;
    }
    if (!file.type.match(/image\/(jpeg|png|jpg|gif)/i)) {
      setImageError("Only image files are allowed (JPEG, PNG, GIF)");
      return false;
    }
    
    setImageError("");
    const previewUrl = URL.createObjectURL(file);
    setFormData({ ...formData, pic1: file, pic1_preview: previewUrl });
    return true;
  };
  
  // Handle image upload from file input
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImage(file);
    }
  };
  
  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.match(/image\/(jpeg|png|jpg|gif)/i)) {
      processImage(file);
    } else {
      setImageError("Please drop a valid image file (PNG, JPG, GIF)");
    }
  };
  
  // Remove image
  const removeImage = () => {
    if (formData.pic1_preview) {
      URL.revokeObjectURL(formData.pic1_preview);
    }
    setFormData({ ...formData, pic1: null, pic1_preview: null });
  };
  
  // Handle form submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      alert("Please enter item name");
      return;
    }
    if (!formData.price || formData.price <= 0) {
      alert("Please enter valid price");
      return;
    }
    if (!formData.main_category) {
      alert("Please select main category");
      return;
    }
    if (!formData.category) {
      alert("Please select category");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append("designation_id", designation_id);
      submitData.append("name", formData.name);
      submitData.append("price", formData.price);
      submitData.append("description", formData.description || "");
      submitData.append("main_category", formData.main_category);
      submitData.append("category", formData.category);
      submitData.append("sub_category", formData.sub_category || "");
      submitData.append("is_veg", formData.is_veg);
      if (formData.pic1) {
        submitData.append("pic1", formData.pic1);
      }
      
      const response = await postData("menu/add_menu_item", submitData);
      
      if (response?.success || response?.status === true) {
        alert("Menu item added successfully!");
        navigate(-1);
      } else {
        alert(response?.message || "Failed to add menu item");
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };
  
  const styles = {
    container: {
      minHeight: "100vh",
      background: ts.background,
      padding: "24px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    backButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "none",
      border: "none",
      color: ts.primary,
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 500,
      marginBottom: "24px",
      padding: "8px 0",
      transition: "all 0.2s",
    },
    card: {
      maxWidth: "800px",
      margin: "0 auto",
      background: ts.surface,
      border: `1px solid ${ts.border}`,
      borderRadius: "20px",
      overflow: "hidden",
    },
    cardHeader: {
      padding: "24px 28px",
      borderBottom: `1px solid ${ts.border}`,
      background: ts.surfaceLighter,
    },
    cardTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: ts.textPrimary,
      margin: 0,
    },
    cardSubtitle: {
      fontSize: "13px",
      color: ts.textMuted,
      marginTop: "6px",
    },
    cardBody: {
      padding: "28px",
    },
    formGroup: {
      marginBottom: "24px",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: ts.textSecondary,
      marginBottom: "8px",
    },
    required: {
      color: ts.error,
      marginLeft: "4px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      background: ts.inputBg,
      border: `1px solid ${ts.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: ts.textPrimary,
      outline: "none",
      transition: "all 0.2s",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "12px 16px",
      background: ts.inputBg,
      border: `1px solid ${ts.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: ts.textPrimary,
      outline: "none",
      cursor: "pointer",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(ts.textMuted)}' strokeWidth='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 16px center",
    },
    textarea: {
      width: "100%",
      padding: "12px 16px",
      background: ts.inputBg,
      border: `1px solid ${ts.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: ts.textPrimary,
      outline: "none",
      resize: "vertical",
      minHeight: "100px",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
    },
    vegNonVeg: {
      display: "flex",
      gap: "16px",
      alignItems: "center",
    },
    vegOption: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      background: formData.is_veg === "yes" ? `${ts.success}15` : ts.surfaceLighter,
      border: `1px solid ${formData.is_veg === "yes" ? ts.success : ts.border}`,
      borderRadius: "12px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    nonVegOption: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      background: formData.is_veg === "no" ? `${ts.error}15` : ts.surfaceLighter,
      border: `1px solid ${formData.is_veg === "no" ? ts.error : ts.border}`,
      borderRadius: "12px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    imageUploadArea: {
      border: `2px dashed ${isDragOver ? ts.primary : ts.border}`,
      borderRadius: "16px",
      padding: "40px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      background: isDragOver ? `${ts.primary}08` : ts.surfaceLighter,
    },
    imagePreviewContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "16px",
    },
    imagePreview: {
      position: "relative",
      display: "inline-block",
    },
    previewImg: {
      width: "150px",
      height: "150px",
      borderRadius: "12px",
      objectFit: "cover",
      border: `2px solid ${ts.border}`,
    },
    removeImageBtn: {
      position: "absolute",
      top: "-10px",
      right: "-10px",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      background: ts.error,
      border: "none",
      color: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s",
    },
    changeImageBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      background: ts.surfaceLighter,
      border: `1px solid ${ts.border}`,
      borderRadius: "10px",
      color: ts.primary,
      fontSize: "13px",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 0.2s",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      marginTop: "32px",
      paddingTop: "24px",
      borderTop: `1px solid ${ts.border}`,
    },
    cancelBtn: {
      padding: "12px 24px",
      background: "transparent",
      border: `1px solid ${ts.border}`,
      borderRadius: "12px",
      color: ts.textSecondary,
      fontWeight: 600,
      fontSize: "14px",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    submitBtn: {
      padding: "12px 32px",
      background: ts.gradient,
      border: "none",
      borderRadius: "12px",
      color: "white",
      fontWeight: 600,
      fontSize: "14px",
      cursor: "pointer",
      transition: "all 0.2s",
      opacity: submitting ? 0.7 : 1,
    },
    errorText: {
      fontSize: "11px",
      color: ts.error,
      marginTop: "6px",
    },
  };
  
  return (
    <div style={styles.container}>
      <button style={styles.backButton} onClick={() => navigate(-1)}>
        <BackIcon /> Back to Menu
      </button>
      
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h1 style={styles.cardTitle}>Add New Menu Item</h1>
          <p style={styles.cardSubtitle}>
            {restaurantName && `Adding item for ${restaurantName}`}
          </p>
        </div>
        
        <div style={styles.cardBody}>
          {/* Name */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Name <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="name"
              style={styles.input}
              placeholder="e.g., Butter Chicken, Masala Dosa, Pizza..."
              value={formData.name}
              onChange={handleChange}
            />
          </div>
          
          {/* Price and Veg/Non-Veg Row */}
          <div style={styles.row}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Price (₹) <span style={styles.required}>*</span>
              </label>
              <input
                type="number"
                name="price"
                style={styles.input}
                placeholder="e.g., 299"
                value={formData.price}
                onChange={handleChange}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Type</label>
              <div style={styles.vegNonVeg}>
                <div
                  style={styles.vegOption}
                  onClick={() => setFormData({ ...formData, is_veg: "yes" })}
                >
                  <VegIcon style={{ color: ts.success }} />
                  <span style={{ fontSize: "13px", fontWeight: 500 }}>Veg</span>
                </div>
                <div
                  style={styles.nonVegOption}
                  onClick={() => setFormData({ ...formData, is_veg: "no" })}
                >
                  <NonVegIcon style={{ color: ts.error }} />
                  <span style={{ fontSize: "13px", fontWeight: 500 }}>Non-Veg</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Description */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              name="description"
              style={styles.textarea}
              placeholder="Describe the item (ingredients, serving size, etc.)..."
              value={formData.description}
              onChange={handleChange}
            />
          </div>
          
          {/* Main Category */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Main Category <span style={styles.required}>*</span>
            </label>
            <select
              name="main_category"
              style={styles.select}
              value={formData.main_category}
              onChange={(e) => handleMainCategoryChange(e.target.value)}
            >
              <option value="">Select or search...</option>
              {mainCategories.map((cat, idx) => (
                <option key={idx} value={cat.name || cat}>
                  {cat.name || cat}
                </option>
              ))}
            </select>
          </div>
          
          {/* Category */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Category <span style={styles.required}>*</span>
            </label>
            <select
              name="category"
              style={styles.select}
              value={formData.category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              disabled={!formData.main_category}
            >
              <option value="">
                {!formData.main_category 
                  ? "Select main category first" 
                  : "Select or search..."
                }
              </option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat.name || cat}>
                  {cat.name || cat}
                </option>
              ))}
            </select>
          </div>
          
          {/* Sub Category */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Sub Category</label>
            <select
              name="sub_category"
              style={styles.select}
              value={formData.sub_category}
              onChange={handleChange}
              disabled={!formData.category}
            >
              <option value="">
                {!formData.category 
                  ? "Select category first" 
                  : "Select or search..."
                }
              </option>
              {subCategories.map((sub, idx) => (
                <option key={idx} value={sub.name || sub}>
                  {sub.name || sub}
                </option>
              ))}
            </select>
          </div>
          
          {/* Image Upload */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Item Image</label>
            {!formData.pic1_preview ? (
              <div 
                style={styles.imageUploadArea}
                onClick={() => document.getElementById('image-upload-input').click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  id="image-upload-input"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <UploadIcon style={{ color: ts.primary, marginBottom: "12px" }} />
                <div style={{ fontSize: "14px", color: ts.textSecondary }}>
                  {isDragOver ? "Drop image here" : "Click or drag to upload image"}
                </div>
                <div style={{ fontSize: "11px", color: ts.textMuted, marginTop: "8px" }}>
                  PNG, JPG, GIF up to 2MB
                </div>
              </div>
            ) : (
              <div style={styles.imagePreviewContainer}>
                <div style={styles.imagePreview}>
                  <img src={formData.pic1_preview} alt="Preview" style={styles.previewImg} />
                  <button 
                    style={styles.removeImageBtn} 
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage();
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
                <div 
                  style={styles.changeImageBtn}
                  onClick={() => document.getElementById('image-upload-input').click()}
                >
                  <UploadIcon style={{ width: "14px", height: "14px" }} />
                  <span>Change Image</span>
                </div>
              </div>
            )}
            {imageError && <div style={styles.errorText}>{imageError}</div>}
          </div>
          
          {/* Buttons */}
          <div style={styles.buttonGroup}>
            <button style={styles.cancelBtn} onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button
              style={styles.submitBtn}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Adding..." : "+ Add Item"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMenuItem;